import { useMutation, useQueryClient } from '@tanstack/react-query';
import { del, get } from '@/shared/api/api-client';

/**
 * Archive (soft-delete) a staff member.
 *   DELETE /api/Staff/{staffSetupId}
 *
 * Mirrors Flutter `deleteStaffById` (settings_data_source_impl.dart:871 —
 * `client.delete('${PracticeService.Staff}/$staffSetupId')`). The row leaves
 * the active StaffList and surfaces under the archived list; the caller
 * refetches the staff-list key on success.
 *
 * Bodiless path DELETE — CONFIRMED via dev cURL 2026-06-02:
 *   DELETE /api/Staff/8591f8e1-59e5-eb11-a7ad-0050f2f18d1f
 */
export function useArchiveStaff() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (staffSetupId: string) =>
      del<unknown>(`/api/Staff/${staffSetupId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'staff-list'] });
    },
  });
}

/**
 * Restore an archived staff member.
 *   GET /api/Staff/{staffSetupId}/UnArchivedStaff   (Flutter parity — GET for a mutation)
 *
 * Mirrors Flutter `unArchiveStaff` (settings_data_source_impl.dart:636 —
 * `client.get('${PracticeService.Staff}/$id/UnArchivedStaff')`). The backend
 * uses a path-style action verb, no body — same idiom as `useUnarchivePractice`.
 * CONFIRMED via dev cURL 2026-06-02:
 *   GET /api/Staff/8591f8e1-59e5-eb11-a7ad-0050f2f18d1f/UnArchivedStaff
 * Note the slash before `UnArchivedStaff` — staff HAS it, doctor does NOT
 * (see use-doctor-archive.ts). Both verified, both verbatim from Flutter.
 */
export function useUnarchiveStaff() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (staffSetupId: string) =>
      get<unknown>(`/api/Staff/${staffSetupId}/UnArchivedStaff`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'staff-list'] });
    },
  });
}
